import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { PreEntry, Section, Category } from '../types';
import PreEntryItem from './PreEntryItem';
import BatchProcessor from './BatchProcessor';
import './PreEntriesList.css';

const PreEntriesList: React.FC = () => {
  const [preEntries, setPreEntries] = useState<PreEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEntries, setSelectedEntries] = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'saved' | 'processed'>('pending');
  const [lastKey, setLastKey] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Sections and categories data
  const [sections, setSections] = useState<Section[] | null>(null);
  const [categoriesCache, setCategoriesCache] = useState<Map<string, Category[]>>(new Map());

  useEffect(() => {
    loadPreEntries(true);
    loadSections();
  }, [statusFilter]);

  const loadSections = async () => {
    try {
      const sectionsData = await apiService.listSections();
      console.log('PreEntriesList - loaded sections:', sectionsData);
      setSections(sectionsData);
    } catch (error) {
      console.error('Error loading sections:', error);
    }
  };

  const loadCategories = async (sectionName: string): Promise<Category[]> => {
    // Check cache first
    if (categoriesCache.has(sectionName)) {
      return categoriesCache.get(sectionName)!;
    }

    try {
      const response = await apiService.listCategories(sectionName);
      const categories = response.categories;
      
      // Cache the result
      setCategoriesCache(prev => new Map(prev).set(sectionName, categories));
      
      return categories;
    } catch (error) {
      console.error('Error loading categories:', error);
      return [];
    }
  };

  const loadPreEntries = async (reset = false) => {
    try {
      if (reset) {
        setLoading(true);
        setLastKey(undefined);
      } else {
        setLoadingMore(true);
      }

      const params = {
        ...(statusFilter !== 'all' && { status: statusFilter }),
        limit: 20,
        ...(lastKey && !reset && { lastKey }),
      };

      const response = await apiService.listPreEntries(params);
      
      if (reset) {
        setPreEntries(response.items);
      } else {
        setPreEntries(prev => [...prev, ...response.items]);
      }

      setLastKey(response.last_key);
      setHasMore(!!response.last_key);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pre-entries');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleEntryUpdate = (updatedEntry: PreEntry) => {
    setPreEntries(prev =>
      prev.map(entry => entry.id === updatedEntry.id ? updatedEntry : entry)
    );
  };

  const handleEntryDelete = (deletedId: string) => {
    setPreEntries(prev => prev.filter(entry => entry.id !== deletedId));
    setSelectedEntries(prev => {
      const newSet = new Set(prev);
      newSet.delete(deletedId);
      return newSet;
    });
  };

  const handleSelectEntry = (entryId: string, selected: boolean) => {
    setSelectedEntries(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(entryId);
      } else {
        newSet.delete(entryId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const selectableEntries = preEntries.filter(entry => entry.status === 'pending' || entry.status === 'saved');
    const allSelected = selectableEntries.every(entry => selectedEntries.has(entry.id));
    
    setSelectedEntries(prev => {
      const newSet = new Set(prev);
      if (allSelected) {
        // Unselect all selectable entries
        selectableEntries.forEach(entry => newSet.delete(entry.id));
      } else {
        // Select all selectable entries
        selectableEntries.forEach(entry => newSet.add(entry.id));
      }
      return newSet;
    });
  };

  const handleBatchProcessComplete = (processedIds: string[]) => {
    // Mark processed entries as processed and clear selection
    setPreEntries(prev =>
      prev.map(entry => 
        processedIds.includes(entry.id) 
          ? { ...entry, status: 'processed' as const, updated_at: new Date().toISOString() }
          : entry
      )
    );
    setSelectedEntries(new Set());
  };

  const selectableEntries = preEntries.filter(entry => entry.status === 'pending' || entry.status === 'saved');
  const selectedSelectableCount = selectableEntries.filter(entry => selectedEntries.has(entry.id)).length;
  const allSelectableSelected = selectableEntries.length > 0 && selectableEntries.every(entry => selectedEntries.has(entry.id));

  if (loading) {
    return (
      <div className="pre-entries-loading">
        <div className="spinner"></div>
        <p>Loading pre-entries...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pre-entries-error">
        <p>Error: {error}</p>
        <button onClick={() => loadPreEntries(true)} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="pre-entries-list">
      {/* Filters and Controls */}
      <div className="list-controls">
        <div className="filter-controls">
          <label htmlFor="status-filter">Status:</label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
          >
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="saved">Saved</option>
            <option value="processed">Processed</option>
          </select>
        </div>

        {statusFilter !== 'processed' && preEntries.length > 0 && (
          <div className="selection-controls">
            <button
              onClick={handleSelectAll}
              className="select-all-button"
              disabled={selectableEntries.length === 0}
            >
              {allSelectableSelected ? 'Unselect All' : 'Select All'}
            </button>
            <span className="selection-count">
              {selectedSelectableCount} of {selectableEntries.length} selected
            </span>
          </div>
        )}
      </div>

      {/* Batch Processing */}
      {selectedEntries.size > 0 && (
        <BatchProcessor
          selectedEntryIds={Array.from(selectedEntries)}
          onProcessComplete={handleBatchProcessComplete}
        />
      )}

      {/* Entries List */}
      {preEntries.length === 0 ? (
        <div className="empty-state">
          <p>No pre-entries found</p>
          <p className="empty-hint">
            {statusFilter === 'pending' 
              ? 'Upload a CSV file to create pre-entries'
              : statusFilter === 'saved'
              ? 'No entries have been saved yet'
              : statusFilter === 'processed'
              ? 'No entries have been processed yet'
              : 'No entries available'
            }
          </p>
        </div>
      ) : (
        <>
          <div className="entries-grid">
            {preEntries.map(entry => (
              <PreEntryItem
                key={entry.id}
                entry={entry}
                selected={selectedEntries.has(entry.id)}
                onSelect={(selected) => handleSelectEntry(entry.id, selected)}
                onUpdate={handleEntryUpdate}
                onDelete={handleEntryDelete}
                showCheckbox={entry.status === 'pending' || entry.status === 'saved'}
                sections={sections}
                loadCategories={loadCategories}
              />
            ))}
          </div>

          {/* Load More */}
          {hasMore && (
            <div className="load-more">
              <button
                onClick={() => loadPreEntries(false)}
                disabled={loadingMore}
                className="load-more-button"
              >
                {loadingMore ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PreEntriesList;